import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnDestroy, SimpleChanges, HostBinding, OnChanges } from '@angular/core';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { CultureService } from '../../services/culture.service';
import { ErrorStateMatcher, MAT_DATE_FORMATS } from '@angular/material/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { SE_UI_DATE_FORMATS } from '../se-ui-date-formats';

export class DateTimePickerStateMatcher implements ErrorStateMatcher {
    error$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    forceError = false;
    required = false;

    isErrorState(control: FormControl | null, _: FormGroupDirective | NgForm | null): boolean {
        let isOnError = this.forceError || !!(control && control.invalid && this.required);
        if (isOnError != this.error$.value) {
            this.error$.next(isOnError);
        }

        return isOnError;
    }
}

@Component({
    selector: 'se-datetime-picker',
    templateUrl: './se-datetime-picker.component.html',
    styleUrls: ['./se-datetime-picker.component.scss'],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: SE_UI_DATE_FORMATS }],
})
export class SeDatetimePickerComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
    @HostBinding('style.display')
    display = 'flex';

    @Input()
    showDateFormatOnHint = false;

    @Input()
    setError = false;

    @Output()
    errorStateChange = new EventEmitter<boolean>();

    isOnError = false;

    /* Two Way */
    @Output() dateTimeChange = new EventEmitter();

    dateTimeValue: moment.Moment | null;
    @Input()
    get dateTime() {
        return this.dateTimeValue;
    }
    set dateTime(value) {
        if (this.dateTimeValue != value) {
            if (value && value.isValid()) {
                this.dateTimeValue = value;

                this.defaultTime =
                    this.dateTime && this.dateTime.isValid() ? this.dateTime.format(this.timeFormat) : null;
                this.defaultDate = this.dateTime && this.dateTime.isValid() ? this.dateTime.clone() : null;
            } else {
                this.defaultTime = null;
                this.defaultDate = null;
                this.dateTimeValue = moment(null);
            }

            this.dateTimeChange.emit(this.dateTimeValue);
        }
    }

    @Output()
    dateTimePickerKeyDown = new EventEmitter<{ ev: KeyboardEvent; picker: 'date' | 'hour' | 'minute' }>();

    @ViewChild('seTimePickerInput', { static: false, read: ElementRef }) timePickerInput: ElementRef;

    @ViewChild('dateInput', { static: false, read: ElementRef }) dateInput: ElementRef;

    @Input()
    disabled: boolean = false;

    @Input()
    showResetButton: boolean = true;

    @Input()
    highlightBackgroundEnabled: boolean = false;

    @Input()
    datePlaceholder: string = this.culture.getShortDatePattern();

    @Input()
    hourPlaceholder: string = 'hh';

    @Input()
    minutePlaceholder: string = 'mm';

    @Input()
    clockAmPm: number = this.getClockFormat(); // 12 per AM PM

    @Input()
    required: boolean = false;

    @Input()
    isWithLabel: boolean = true;

    @Input()
    label = this.translateService.instant('Date') + ' & ' + this.translateService.instant('Time');

    @Input()
    customPanelClass = '';

    // Members

    private isOnErrorSubscriber: Subscription;

    defaultTime: string;
    defaultDate: moment.Moment = moment(null);

    timeFormat: string = this.culture.getShortTimePattern();
    dateFormat: string = this.culture.getShortDatePattern();

    dateTimePickerMatcher: DateTimePickerStateMatcher = new DateTimePickerStateMatcher();

    constructor(private translateService: TranslateService, private culture: CultureService) {}

    ngAfterViewInit(): void {
        try {
            // Imposto i placeholder
            let timePickerInputs = this.getTimePickerInput();
            // Evento KeyDown
            timePickerInputs[0].addEventListener('keydown', this.onHourKeyDown);
            timePickerInputs[1].addEventListener('keydown', this.onMinuteKeyDown);

            timePickerInputs[0].placeholder = this.hourPlaceholder;
            timePickerInputs[1].placeholder = this.minutePlaceholder;
        } catch (err) {
            console.error(err);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasError) {
            this.dateTimePickerMatcher.forceError = !!changes.hasError.currentValue;
        }
    }

    ngOnDestroy() {
        try {
            // Rimuovo l'eveno per evitare leaks...
            let timePickerInputs = this.getTimePickerInput();
            timePickerInputs[0].removeEventListener('keydown', this.onHourKeyDown);
            timePickerInputs[1].removeEventListener('keydown', this.onMinuteKeyDown);
        }
        catch (err) {
            console.error(err);
        }

        if (this.isOnErrorSubscriber) {
            this.isOnErrorSubscriber.unsubscribe();
        }

    }

    ngOnInit(): void {
        if (this.dateTime && this.dateTime.isValid()) {
            this.defaultTime = this.dateTime.format(this.timeFormat);
            this.defaultDate = this.dateTime.clone();
        } else {
            this.dateTimeValue = moment(null);
        }

        // Se � required attivo il sistema di errore
        if (this.required) {
            this.dateTimePickerMatcher.required = this.required;
            this.isOnErrorSubscriber = this.dateTimePickerMatcher.error$.subscribe((value) => {
                if (this.isOnError != value) {
                    this.isOnError = value;
                    setTimeout(() => {
                        // Il timeout serve per la sincronizzazione altrimenti abbiamo l'errore i debug
                        this.errorStateChange.emit(value);
                    }, 0);
                }
            });
        }
    }

    private fixTimePickerBug(hour: number, minute: number) {
        try {
            let timePickerInputs = this.getTimePickerInput();
            timePickerInputs[0].value = hour.toString().padStart(2, '0');
            timePickerInputs[1].value = minute.toString().padStart(2, '0');
        } catch(err) {
            console.error(err);
        }
    }

    private fixTimePickerBug1() {
        try {
            let timePickerInputs = this.getTimePickerInput();
            timePickerInputs[0].value = null;
            timePickerInputs[1].value = null;
        } catch (err) {
            console.error(err);
        }
    }

    private getDateTimeDefault() {
        // Oggi alle 12
        const defaultTime = moment();
        defaultTime.hour(12);
        defaultTime.minute(0);
        defaultTime.second(0);

        return defaultTime.clone();
    }

    moveFocusOMinute() {
        try {
            let timePickerInputs = this.getTimePickerInput();
            timePickerInputs[1].focus();
        } catch (err) {
            console.error(err);
        }
    }

    private setNow() {
        this.defaultDate = moment(Date.now());
        this.onDateChanging({ value: this.defaultDate });

        this.defaultTime = moment(Date.now()).format(this.timeFormat);
        this.onTimeChanging(this.defaultTime);
    }

    private getClockFormat() {
        if (this.culture.getCulture() == 'en-US') return 12;

        return 24;
    }

    getErrorMessage(): string {
        return this.translateService.instant('MandatoryData');
    }

    onDateTimeKeydown(e) {
        this.dateTimePickerKeyDown.emit({
            ev: e,
            picker: 'date',
        });

        switch (e.key) {
            case ' ':
                e.preventDefault();
                this.setNow();
                break;
            case 'Delete':
                this.onResetDateTime();
                this.fixTimePickerBug1();
                break;
            case 'ArrowUp':
                e.preventDefault();

                if (!this.defaultDate || !this.defaultDate.isValid()) {
                    this.setNow();
                } else {
                    this.defaultDate = moment(this.defaultDate.add(1, 'day'));
                    this.onDateChanging({ value: this.defaultDate });
                }
                break;
            case 'ArrowDown':
                e.preventDefault();

                if (!this.defaultDate || !this.defaultDate.isValid()) {
                    this.setNow();
                } else {
                    this.defaultDate = moment(this.defaultDate.add(-1, 'day'));
                    this.onDateChanging({ value: this.defaultDate });
                }
                break;
            default:
                break;
        }
    }

    onResetDateTime() {
        this.fixTimePickerBug1();

        this.dateTime = moment(null);
    }

    onHourKeyDown = (event: KeyboardEvent): void => {
        this.dateTimePickerKeyDown.emit({
            ev: event,
            picker: 'hour',
        });

        if (!event.shiftKey && event.key == 'Tab') {
            setTimeout(() => {
                this.moveFocusOMinute();
            }, 0);
        } else
            switch (event.key) {
                case ' ':
                    if (!this.defaultDate || !this.defaultDate.isValid()) {
                        this.setNow();
                    }
                    break;
                case 'Delete':
                    this.onResetDateTime();
                    break;
                default:
                    break;
            }
    };

    onMinuteKeyDown = (event: KeyboardEvent): void => {
        this.dateTimePickerKeyDown.emit({
            ev: event,
            picker: 'minute',
        });

        switch (event.key) {
            case ' ':
                if (!this.dateTime || !this.dateTime.isValid()) {
                    event.preventDefault();
                    this.setNow();
                }
                break;
            case 'Delete':
                this.onResetDateTime();
                this.fixTimePickerBug1();
                break;
            default:
                break;
        }
    };

    onTimeChanging(value, fixBug: boolean = true) {
        let newDateTime: moment.Moment;

        if (value) {
            // Se � valido prendo quello impostato altrimenti oggi
            const newDate =
                this.dateTime && this.dateTime.isValid()
                    ? moment(this.dateTime, this.dateFormat)
                    : this.getDateTimeDefault();

            newDateTime = newDate.clone();

            const newTime = moment(value, this.timeFormat);
            newDateTime.hour(newTime.hour());
            newDateTime.minute(newTime.minute());
            newDateTime.second(newTime.second());

            if (fixBug) this.fixTimePickerBug(newTime.hour(), newTime.minute());
        } else newDateTime = moment(null); // now

        this.dateTime = newDateTime.clone();
    }

    onDateChanging(value) {
        let newDateTime: moment.Moment;

        if (value && value.value) {
            newDateTime = value.value.clone(); // imposto la data

            // Se � valido prendo quello impostato altrimenti le 12
            const newTime =
                this.dateTime && this.dateTime.isValid()
                    ? moment(this.dateTime, this.timeFormat)
                    : this.getDateTimeDefault();

            // finisco la composizione del nuovo date time
            newDateTime.hour(newTime.hour());
            newDateTime.minute(newTime.minute());
            newDateTime.second(newTime.second());
        } else newDateTime = moment(null); // now

        this.dateTime = newDateTime.clone();
    }

    // Funzioni di manipolazione dall'esterno

    focus() {
        this.dateInput.nativeElement.focus();
    }

    focusHour() {
        setTimeout(() => {
            try {
                const timePickerInputs = this.getTimePickerInput();
                timePickerInputs[0].focus();
            } catch (err) {
                console.error(err);
            }
        }, 0);
    }

    select() {
        this.dateInput.nativeElement.select();
    }

    getDateFormat() {
        return SE_UI_DATE_FORMATS.display.dateInput;
    }

    getTimePickerInput = () => {
        let timepickerDiv = this.timePickerInput.nativeElement.querySelector('div.ngx-timepicker');
        return timepickerDiv.querySelectorAll('input');
    }
}
